const STORAGE_KEY = 'biometric-calculator-logs';
const STORAGE_VERSION = 2;
const MAX_STORAGE_DAYS = 45;

function trimDaysToMax(days) {
  const keys = Object.keys(days).filter(Boolean).sort();
  if (keys.length <= MAX_STORAGE_DAYS) return days;
  const toRemove = keys.length - MAX_STORAGE_DAYS;
  const next = { ...days };
  for (let i = 0; i < toRemove; i++) delete next[keys[i]];
  return next;
}

/**
 * Generate a stable, unique string ID.
 * Falls back to a timestamp+random string if crypto is unavailable (e.g. non-HTTPS).
 */
function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Ensure every session in every day has a valid, non-empty `id`.
 * This repairs data written by older versions of the app that stored sessions
 * without IDs, which caused nextId = NaN and made all deletions silently fail.
 */
function repairMissingIds(days) {
  let mutated = false;
  const repairedDays = {};
  for (const [date, sessions] of Object.entries(days)) {
    if (!Array.isArray(sessions)) {
      repairedDays[date] = sessions;
      continue;
    }
    const repairedSessions = [];
    for (const s of sessions) {
      const idMissing = s.id == null || (typeof s.id === 'number' && Number.isNaN(s.id));
      if (idMissing) {
        mutated = true;
        repairedSessions.push({ ...s, id: generateId() });
      } else {
        repairedSessions.push(s);
      }
    }
    repairedDays[date] = repairedSessions;
  }
  return { days: repairedDays, mutated };
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    const expectedWorkHours = typeof data?.expectedWorkHours === 'number'
      ? Math.max(0, Math.min(24, data.expectedWorkHours))
      : 8;
    if (!data || data.version !== STORAGE_VERSION) {
      const migrated = (data?.version === 1 && Array.isArray(data.sessions) && data.sessions.length > 0)
        ? { [getDateKey(new Date())]: data.sessions }
        : {};
      const { days: repairedDays, mutated } = repairMissingIds(migrated);
      const trimmed = trimDaysToMax(repairedDays);
      if (mutated) {
        // Persist the repaired data so old IDs are not regenerated on next load.
        saveToStorage(trimmed, expectedWorkHours);
      }
      return { days: trimmed, expectedWorkHours };
    }
    if (!data.days || typeof data.days !== 'object') {
      return { days: {}, expectedWorkHours };
    }
    const { days: repairedDays, mutated } = repairMissingIds(data.days);
    const days = trimDaysToMax(repairedDays);
    if (mutated) {
      saveToStorage(days, expectedWorkHours);
    }
    return { days, expectedWorkHours };
  } catch {
    return null;
  }
}

function saveToStorage(days, expectedWorkHours) {
  try {
    const trimmed = trimDaysToMax(days || {});
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: STORAGE_VERSION,
      expectedWorkHours,
      days: trimmed,
    }));
  } catch (e) {
    console.warn('Could not save punch logs to localStorage', e);
  }
}

function getDateKey(date) {
  const d = date instanceof Date ? date : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

class LocalAttendanceRepository {
  constructor() {
    this.days = {};
    this.expectedWorkHours = 8;
    this._load();
  }

  _load() {
    const loaded = loadFromStorage();
    if (loaded && loaded.days) {
      this.days = loaded.days;
      this.expectedWorkHours = loaded.expectedWorkHours;
    }
  }

  _persist() {
    saveToStorage(this.days, this.expectedWorkHours);
  }

  getTodaySessions() {
    const key = getDateKey(new Date());
    return (this.days[key] || []).slice();
  }

  getAllDays() {
    return { ...this.days };
  }

  getAvailableDates() {
    return Object.keys(this.days).filter(Boolean).sort();
  }

  getExpectedWorkHours() {
    return this.expectedWorkHours;
  }

  setExpectedWorkHours(value) {
    this.expectedWorkHours = Math.max(0, Math.min(24, Number(value) || 0));
    this._persist();
  }

  setSessions(updater) {
    const today = getDateKey(new Date());
    const current = this.days[today] || [];
    this.days = { ...this.days };
    this.days[today] = typeof updater === 'function' ? updater(current) : updater;
    this._persist();
  }

  punchIn() {
    if (this.isInside()) return null;
    const now = new Date();
    const h = now.getHours();
    const m = now.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    let hour12 = h % 12;
    if (hour12 === 0) hour12 = 12;
    const timeStr = `${String(hour12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
    const session = { id: generateId(), inTime: timeStr, outTime: null };
    this.setSessions((prev) => [...prev, session]);
    return 'Punched in';
  }

  punchOut() {
    if (!this.isInside()) return null;
    const now = new Date();
    const h = now.getHours();
    const m = now.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    let hour12 = h % 12;
    if (hour12 === 0) hour12 = 12;
    const timeStr = `${String(hour12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
    this.setSessions((prev) => {
      const last = prev.length - 1;
      if (last < 0 || prev[last].outTime != null) return prev;
      return [
        ...prev.slice(0, last),
        { ...prev[last], outTime: timeStr },
      ];
    });
    return 'Punched out';
  }

  isInside() {
    const sessions = this.getTodaySessions();
    return sessions.length > 0 && sessions[sessions.length - 1].outTime == null;
  }

  addPunch() {
    const session = { id: generateId(), inTime: '09:00 AM', outTime: '06:00 PM' };
    this.setSessions((prev) => [...prev, session]);
    return 'Added punch entry';
  }

  removePunch(id) {
    const target = String(id);
    this.setSessions((prev) => prev.filter((s) => String(s.id) !== target));
    return 'Removed entry';
  }

  updateSession(id, field, value) {
    this.setSessions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    );
  }

  addSessionsToToday(sessions) {
    const sessionsWithIds = sessions.map((s) => ({
      id: generateId(),
      inTime: s.inTime,
      outTime: s.outTime != null ? s.outTime : null,
    }));
    this.setSessions((prev) => [...prev, ...sessionsWithIds]);
    return sessionsWithIds;
  }
}

export default LocalAttendanceRepository;
